import { useEffect, useState, type ReactNode } from 'react';
import { Drawer } from 'vaul';
import { cn } from './cn.js';
import { Dialog } from './Dialog.js';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  variant?: 'bottom' | 'full';
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

function useIsDesktop(): boolean {
  const get = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;
  const [v, setV] = useState<boolean>(get);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const h = (e: MediaQueryListEvent) => setV(e.matches);
    setV(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', h);
      return () => mql.removeEventListener('change', h);
    }
    mql.addListener(h);
    return () => mql.removeListener(h);
  }, []);
  return v;
}

export function Sheet({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  variant = 'bottom',
  size = 'lg',
}: SheetProps) {
  const desktop = useIsDesktop();

  if (desktop) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title={title}
        description={description}
        footer={footer}
        className={className}
        size={size}
      >
        {children}
      </Dialog>
    );
  }

  return (
    <Drawer.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-overlay backdrop-blur-sm" />
        <Drawer.Content
          className={cn(
            'fixed bottom-0 left-0 right-0 z-50 flex flex-col rounded-t-3xl border-t border-line bg-surface-2 outline-none',
            variant === 'full' ? 'h-[96dvh]' : 'max-h-[92dvh]',
            className,
          )}
        >
          <Drawer.Title className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</Drawer.Title>
          <div className="mx-auto my-3 h-1.5 w-12 rounded-full bg-raise-3" />
          {(title || description) && (
            <div className="px-6 pb-3 pt-1">
              {title && (
                <h2 className="text-xl font-semibold tracking-tight text-text">{title}</h2>
              )}
              {description && (
                <p className="mt-1 text-sm text-text-dim">{description}</p>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-6 pb-6">{children}</div>
          {footer && (
            <div className="border-t border-line bg-surface-2/80 px-6 py-4 pb-[max(env(safe-area-inset-bottom),1rem)] backdrop-blur">
              {footer}
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
